// frontend/src/api/shopApi.js
// Mythic Shop V1 API client.
// Live calls: PR5A player-variant ownership endpoints (/api/v2/player-variants/).
// Live calls: PR6 shop-state endpoints (/api/v2/shop/).
// Pattern follows baseballApi.js: loud errors, credentials: 'include',
// getCsrfHeaders() on POST, JSDoc on every export.

import { API_BASE, getCsrfHeaders } from './transport';

const VARIANTS_BASE = `${API_BASE}/v2/player-variants`;

// ---------------------------------------------------------------------------
// Sentinel error classes
// ---------------------------------------------------------------------------

/**
 * Thrown by getShopState() when the mythril_shop feature flag is DISABLED.
 * ShopPage renders a graceful "coming soon" state instead of a crash screen.
 */
export class ShopFeatureDisabledError extends Error {
    constructor(message) {
        super(message);
        this.name = 'ShopFeatureDisabledError';
    }
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Parse a shop API response. Throws loudly on non-2xx.
 * @param {Response} response
 * @param {string} context
 */
async function parseShopResponse(response, context) {
    if (!response.ok) {
        let errorMsg = `HTTP ${response.status}`;
        let errorCode = null;
        try {
            const errorData = await response.json();
            errorMsg = errorData.error || errorData.message || errorData.detail || errorMsg;
            errorCode = errorData.error_code ?? null;
        } catch (_) {
            try {
                const rawText = await response.text();
                if (rawText) errorMsg = `${errorMsg}: ${rawText}`;
            } catch (__) {
                // cannot read body — use status only
            }
        }
        // Fix 3b: attach status and error_code to the thrown Error so callers can
        // read err.status directly instead of parsing the message string.
        const err = new Error(`❌ shopApi [${context}]: ${errorMsg}`);
        err.status = response.status;
        err.error_code = errorCode;
        throw err;
    }
    return response.json();
}

// ---------------------------------------------------------------------------
// Image path helpers (exported) — Fix 8: flat folder paths, no theme subdir
// ---------------------------------------------------------------------------

/**
 * Base cartoon images — flat folder, no theme subdirectory.
 * @param {string} gsisId — e.g. '00-0023459'
 * @returns {string}
 */
export function getDefaultPlayerImageUrl(gsisId) {
    // Base cartoon images
    return `/art/player_images_gsis_cartoon/${gsisId}.png`;
}

/**
 * Unlockable variant/misprint images — flat folder, no theme subdir.
 * @param {string} gsisId — e.g. '00-0023459'
 * @returns {string}
 */
export function getVariantImageUrl(gsisId) {
    // Unlockable variant/misprint images (flat folder, no theme subdir)
    return `/art/player_images_gsis_variants/${gsisId}.png`;
}

/**
 * Legacy/original player images — flat folder.
 * @param {string} gsisId — e.g. '00-0023459'
 * @returns {string}
 */
export function getLegacyPlayerImageUrl(gsisId) {
    // Legacy/original player images
    return `/art/player_images_gsis/${gsisId}.png`;
}

/**
 * Resolve the correct image URL based on themeKey string.
 * Maps to a flat art folder — no themeKey subfolder.
 * @param {string} gsisId          — player GSIS ID
 * @param {string|null} themeKey   — 'cartoon'|'variant'|'misprint'|'wizard'|'legacy'|'base'|null
 * @returns {string}
 */
export function resolvePlayerImageUrl(gsisId, themeKey = null) {
    if (!gsisId) {
        throw new Error(
            `❌ shopApi [resolvePlayerImageUrl]: gsisId is required. Got: ${JSON.stringify({ gsisId, themeKey })}`
        );
    }
    if (!themeKey || themeKey === 'default' || themeKey === 'cartoon') {
        return getDefaultPlayerImageUrl(gsisId);
    }
    if (themeKey === 'variant' || themeKey === 'misprint' || themeKey === 'wizard') {
        return getVariantImageUrl(gsisId);
    }
    if (themeKey === 'legacy' || themeKey === 'base') {
        return getLegacyPlayerImageUrl(gsisId);
    }
    // Unknown theme — fall back to cartoon
    return getDefaultPlayerImageUrl(gsisId);
}

/** Fallback image when player art is missing */
export const FALLBACK_PLAYER_IMAGE = '/art/mystery-item2.png';

// ---------------------------------------------------------------------------
// Live API calls — PR5A (these endpoints exist now)
// ---------------------------------------------------------------------------

/**
 * GET /api/v2/player-variants/catalog/
 * Returns the full variant catalog available to the user.
 */
export function getVariantCatalog() {
    return fetch(`${VARIANTS_BASE}/catalog/`, {
        method: 'GET',
        credentials: 'include',
    }).then(response => parseShopResponse(response, 'getVariantCatalog'));
}

/**
 * GET /api/v2/player-variants/owned/
 * Returns all variant ownership records for the authenticated user.
 */
export function getOwnedVariants() {
    return fetch(`${VARIANTS_BASE}/owned/`, {
        method: 'GET',
        credentials: 'include',
    }).then(response => parseShopResponse(response, 'getOwnedVariants'));
}

/**
 * POST /api/v2/player-variants/equip/
 * Equips a variant by ownership ID. Throws loudly if ownershipId is falsy.
 * Fix 4: intercepts mock ownership_ids (contain 'mock') and returns mock success
 * so the live endpoint is not called with fake IDs.
 * @param {string} ownershipId — REQUIRED: the ownership_id from the outcome payload
 * @returns {Promise<object>}
 */
export async function equipVariant(ownershipId, gsisId) {
    if (!ownershipId) {
        throw new Error(
            '❌ shopApi [equipVariant]: ownershipId is required — cannot equip without a valid ownership_id'
        );
    }
    // Fix 4: intercept mock ownership_ids — real IDs will be UUIDs (no 'mock' substring)
    if (String(ownershipId).includes('mock')) {
        // MOCK — pending PR6 backend: real ownership_id will be a UUID
        await new Promise(r => setTimeout(r, 500));
        return {
            success: true,
            equipped: {
                gsis_id: gsisId,
                ownership_id: ownershipId,
                variant_id: null,
                catalog_key: null,
                theme_key: null,
                theme_name: null,
                finish: null,
                image_url: null,
            }
        };
    }
    // Live call for real ownership_ids (PR5 TDD §7.4)
    const response = await fetch(`${VARIANTS_BASE}/equip/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify({ gsis_id: gsisId, ownership_id: ownershipId }),
    });
    if (!response.ok) throw new Error(`Equip failed: ${response.status}`);
    return response.json();
}

// ---------------------------------------------------------------------------
// Offer key translation — frontend keys → backend keys
// ---------------------------------------------------------------------------

const OFFER_KEY_MAP = {
    'printer':     'player_printer',
    'variantPack': 'static_variant_pack',
};

// ---------------------------------------------------------------------------
// MOCK — pending real IAP backend: mockPurchaseMythril (Fix 3)
// ---------------------------------------------------------------------------

/**
 * MOCK — pending real IAP backend
 * Simulates a Mythril bundle purchase with a short network delay.
 * @param {number} mythrilAmount — amount of Mythril to award
 * @returns {Promise<{ success: boolean, mythrilAwarded: number }>}
 */
export async function mockPurchaseMythril(mythrilAmount) {
    // MOCK — pending real IAP backend
    await new Promise(r => setTimeout(r, 400)); // simulate network delay
    return { success: true, mythrilAwarded: mythrilAmount };
}

/**
 * POST /api/v2/shop/daily-reward/claim/
 * Claims the daily 100-Mythril early access reward.
 * Returns 200 on both first claim (granted_now: true) and replay (granted_now: false).
 * The `balance` field always reflects the current wallet balance after the operation.
 *
 * @returns {Promise<{
 *   reward_key: string,
 *   cycle_date: string,
 *   amount: number,
 *   granted_now: boolean,
 *   replayed: boolean,
 *   balance: number,
 *   daily_reset_at: string,
 * }>}
 */
export async function claimDailyStipend() {
    const response = await fetch(`${API_BASE}/v2/shop/daily-reward/claim/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify({}),
    });
    return parseShopResponse(response, 'claimDailyStipend');
}

// ---------------------------------------------------------------------------
// Live API calls — PR6 shop-state endpoints
// ---------------------------------------------------------------------------

/**
 * GET /api/v2/shop/
 * Returns the current shop state for the authenticated user.
 * Maps the live response to the shape ShopPage.js expects.
 * Also passes through _balance so ShopPage can sync AuthContext on load.
 *
 * @returns {Promise<{
 *   punchCount: number,
 *   punchCredits: number,
 *   dailyFreeConsumed: boolean,
 *   hasFreeAvailable: boolean,
 *   pullsUsedToday: number,
 *   printerLocked: boolean,
 *   _balance: number,
 *   _daily_reset_at: string,
 *   _offers: Array<object>,
 * }>}
 */
export async function getShopState() {
    const response = await fetch(`${API_BASE}/v2/shop/`, {
        method: 'GET',
        credentials: 'include',
    });

    // Feature flag disabled — graceful, not a crash
    if (response.status === 404) {
        let errorCode = null;
        try {
            const body = await response.json();
            errorCode = body.error_code;
        } catch (_) { /* ignore parse failure */ }
        if (errorCode === 'MYTHRIL_SHOP_FEATURE_DISABLED') {
            throw new ShopFeatureDisabledError('Mythril Shop is not available yet.');
        }
    }

    const data = await parseShopResponse(response, 'getShopState');

    const printerOffer = data.offers?.find(o => o.offer_key === 'player_printer');
    // Fix 4: GET /api/v2/shop/ uses next_entitlement (not entitlement).
    // POST /api/v2/shop/open/ uses entitlement — kept as-is in openShopOffer().
    const hasFreeAvailable =
        printerOffer?.next_entitlement === 'DAILY_FREE' ||
        printerOffer?.next_entitlement === 'PUNCH_FREE';

    return {
        punchCount:        data.punch?.progress ?? 0,
        punchCredits:      data.punch?.free_credits ?? 0,
        dailyFreeConsumed: printerOffer?.next_entitlement !== 'DAILY_FREE',
        hasFreeAvailable,
        pullsUsedToday:    printerOffer?.paid_opening_count ?? 0,
        printerLocked:     printerOffer?.available === false,
        // Fix 1: expose backend next_price as authoritative cost source
        printerNextPrice:  printerOffer?.next_price ?? null,
        packNextPrice:     data.offers?.find(o => o.offer_key === 'static_variant_pack')?.next_price ?? null,
        // Pass through raw balance so ShopPage can sync AuthContext on load
        _balance:          data.balance,
        // Pass through server reset time for countdown
        _daily_reset_at:   data.daily_reset_at,
        // Pass through offer display metadata for dynamic card titles
        _offers:           data.offers ?? [],
        // Pass through daily reward eligibility for MythrilPurchaseModal
        _daily_reward:     data.daily_reward ?? null,
    };
}

/**
 * POST /api/v2/shop/open/
 * Opens a shop offer (printer pull or variant pack).
 * Translates frontend offer keys ('printer'/'variantPack') to backend keys.
 * Throttled at 30/minute per user on the backend.
 *
 * @param {'printer'|'variantPack'} offerKey
 * @param {string} requestId — UUID generated by the caller (crypto.randomUUID())
 * @returns {Promise<{
 *   success: boolean,
 *   receipt: { offer_key: string, request_id: string, cards: Array<object>, balance_after: number },
 *   punchAdded: boolean,
 *   newPunchCount: number,
 *   freeGranted: boolean,
 *   usedFree: boolean,
 *   updatedShopState: object,
 *   replayed: boolean,
 * }>}
 */
export async function openShopOffer(offerKey, requestId) {
    const backendOfferKey = OFFER_KEY_MAP[offerKey];
    if (!backendOfferKey) {
        throw new Error(
            `❌ shopApi [openShopOffer]: offerKey must be 'printer' or 'variantPack'. Got: ${JSON.stringify(offerKey)}`
        );
    }

    const response = await fetch(`${API_BASE}/v2/shop/open/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify({ offer_key: backendOfferKey, request_id: requestId }),
    });
    const data = await parseShopResponse(response, 'openShopOffer');

    // Map outcomes[] → receipt.cards[]
    // Backend serializer field: player_name_snapshot (confirmed: mythril_shop_serializers.py line 109)
    const cards = (data.outcomes ?? []).map(outcome => ({
        ownership_id:         outcome.ownership_id,
        gsis_id:              outcome.gsis_id,
        player_name_snapshot: outcome.player_name_snapshot,
        position:             outcome.position,
        team:                 outcome.team_abbr,
        team_name_snapshot:   outcome.team_name_snapshot ?? '',
        theme_key:            outcome.theme_key,
        image_url:            outcome.image_url ?? null,
        is_duplicate:         outcome.was_duplicate ?? false,
    }));

    // Derive punch state from response
    const punch = data.punch ?? {};
    const newPunchCount = punch.progress ?? 0;
    const punchCredits  = punch.free_credits ?? 0;
    const usedFree      = data.entitlement === 'DAILY_FREE' || data.entitlement === 'PUNCH_FREE';
    // punchAdded: a duplicate was received (duplicates earn punch stamps)
    const punchAdded    = cards.some(c => c.is_duplicate);
    // freeGranted: punch credits increased — detect via punches_after < punches_before rollover on any outcome
    const freeGranted   = (data.outcomes ?? []).some(
        o => typeof o.punches_before === 'number' && typeof o.punches_after === 'number' && o.punches_after < o.punches_before
    );

    const updatedShopState = {
        punchCount:        newPunchCount,
        punchCredits,
        dailyFreeConsumed: data.entitlement !== 'DAILY_FREE',
        hasFreeAvailable:  punchCredits > 0,
        // pullsUsedToday is refreshed via a follow-up getShopState() call in ShopPage.js
        pullsUsedToday:    0,
        printerLocked:     false,
    };

    return {
        success: true,
        receipt: {
            offer_key:    data.offer_key,
            request_id:   data.request_id,
            cards,
            balance_after: data.balance_after,
        },
        punchAdded,
        newPunchCount,
        freeGranted,
        usedFree,
        updatedShopState,
        replayed: data.replayed ?? false,
    };
}
